import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import DataPoolBuyBackground from '../components/backgrounds/DataPoolBuyBackground';
import BuyDataPoolSection from '../components/sections/BuyDataPoolSection';
import RowDataCard from '../components/cards/RowDataCard';
import MainButton from '../components/buttons/MainButton';
import InputModal from '../components/modals/InputModal';

import { getDataPoolUploads } from '../apis/DataUpload';
import { getDataPoolInfo } from '../apis/DataPoolInfo';

import DataPopOut from '../components/popout/DataPopOut';
import { useData } from '../context/DataContext';

const BuyDataPool = () => {
    const location = useLocation();
    const path = location.pathname.split("/");
    const dataPoolId = path[path.length - 1];
    const { dataInfo, setDataInfo } = useData();
    const [uploads, setUploads] = useState([]);
    const [dataPoolInfo, setDataPoolInfo] = useState(null);
    const [minDataQuality, setMinDataQuality] = useState(null);
    const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

    const openFilterModal = () => {
        setIsFilterModalOpen(true);
    }

    const closeFilterModal = () => {
        setIsFilterModalOpen(false);
    }

    const filteredUploads = () => {
        return uploads.filter(upload => upload.dataQualityScore >= (minDataQuality==null ? 0 : minDataQuality))
    }

    const fetchDataUploads = useCallback(async () => {
        const dataPoolUploads = await getDataPoolUploads(dataPoolId);
        if (dataPoolUploads) {
            setUploads(dataPoolUploads);
        }
    }, [dataPoolId]);

    const getDataPool = useCallback(async () => {
        const dataPoolInfo = await getDataPoolInfo(dataPoolId);
        if (dataPoolInfo) {
            setDataPoolInfo(dataPoolInfo);
        }
    }, [dataPoolId]);

    useEffect(() => {
        getDataPool();
        fetchDataUploads();
    }, [getDataPool, fetchDataUploads]);

    // Scroll to top when the component mounts
    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const toggleSelectedUpload = (upload) => {
        setDataInfo(prevSelectedUpload => {
            const index = prevSelectedUpload.findIndex(item => item.uploadHash === upload.uploadHash);
            if (index !== -1) {
                // Remove the upload from selected uploads
                return prevSelectedUpload.filter(item => item.uploadHash !== upload.uploadHash);
            } else {
                // Add the upload to selected uploads
                return [...prevSelectedUpload, { uploadHash: upload.uploadHash, dataQualityScore: upload.dataQualityScore, price: upload.dataQualityScore * dataPoolInfo.price, dataPoolId: dataPoolInfo.dataPoolId }];
            }
        });
    };

    const checkSelectedAll = () => {
        return uploads.every(upload => dataInfo.some(item => item.uploadHash === upload.uploadHash));
    }

    const reverseSelectAll = () => {
        if (dataInfo.length > 0) {
            setDataInfo(prevSelectedUpload => {
                return prevSelectedUpload.filter(item => item.dataPoolId !== dataPoolInfo.dataPoolId);
            }
        )}
    }

    const handleSelectAll = () => {
        if (uploads.length > 0) {
            setDataInfo(prevSelectedUpload => {
                const selectedUploads = prevSelectedUpload.map(item => item.uploadHash);
                const newSelectedUploads = filteredUploads().filter(upload => !selectedUploads.includes(upload.uploadHash));
                return [...prevSelectedUpload, ...newSelectedUploads.map(upload => ({
                    uploadHash: upload.uploadHash,
                    dataQualityScore: upload.dataQualityScore,  
                    price: upload.dataQualityScore * dataPoolInfo.price,
                    dataPoolId: dataPoolInfo.dataPoolId
                }))];
            });
        }
    }

    return (
        <DataPoolBuyBackground>
            {dataPoolInfo &&
                <BuyDataPoolSection>
                    <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        width: '88vw',
                    }}>
                        <div>
                            <h1>{dataPoolInfo.dataPoolName} Data Pool</h1>
                            <h4>
                                Pay per upload based on the data quality <br />
                                Max price per upload: {dataPoolInfo.price * 10}
                            </h4>
                        </div>
                        <div style={{height: '1rm', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end'}}>
                            <div style={{display: 'flex', flexDirection: 'row', gap: '10px'}}>
                            {/* Button may be changed to icon for a more simplified UI :)*/}
                            <MainButton
                                header="Filter"
                                subheader="Filter based on data quality score"
                                onClick={openFilterModal}
                            />
                            <MainButton 
                                header={checkSelectedAll() ? 'Deselect All' : 'Select All'}
                                subheader={(checkSelectedAll() ? "Deselect" : "Select")  + " all uploads in this data pool"}
                                onClick={checkSelectedAll() ? reverseSelectAll : handleSelectAll}
                            />
                            </div>
                        </div>
                    </div>
                    {isFilterModalOpen && (
                        <InputModal
                        header="Filter Data Quality"
                        subheader="Enter the minimum data quality score (0~10)"
                        inputLabel="Data Quality Score"
                        input={minDataQuality}
                        setInput={setMinDataQuality}
                        onClose={closeFilterModal} // Pass close function to modal
                        />
                    )}
                    {dataInfo?.length > 0 && <DataPopOut uploads={dataInfo} />}
                    {dataPoolInfo && uploads.length > 0 && (
                        <div className='upload-container'>
                            {filteredUploads()
                                .map((upload, index) => (
                                    <RowDataCard
                                        key={index}
                                        title={upload.dataQualityScore * dataPoolInfo.price}
                                        uploadHash={upload.uploadHash}
                                        description={upload.dataQualityScore}
                                        onClick={() => toggleSelectedUpload(upload)}
                                        selected={dataInfo.some(item => item.uploadHash === upload.uploadHash)}
                                        time={upload.timestamp}
                                    />
                            ))}
                        </div>
                    )}
                </BuyDataPoolSection>
            }
        </DataPoolBuyBackground>
    );
};

export default BuyDataPool;
